import React, { useEffect, useRef, useState } from "react";
import { PhosphorIcon } from "./PhosphorIcon";
import "../styles/statbar.css";

/**
 * StatBar — eye-catching achievement strip with count-up animation that
 * triggers when scrolled into view. Teal band, icons, accessible.
 */
const STATS = [
  { icon: "PlugsConnected", target: 300, suffix: "+", label: "Integrations delivered" },
  { icon: "Clock", target: 30, prefix: "15–", suffix: " days", label: "Typical go-live", noCount: true, display: "15–30 days" },
  { icon: "ShieldCheck", target: 98.9, suffix: "%", label: "Client satisfaction", decimals: 1 },
  { icon: "UsersThree", target: 40, suffix: "+", label: "Certified consultants" },
];

function useCountUp(target, run, decimals = 0, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target, decimals, duration]);
  return decimals ? val.toFixed(decimals) : Math.round(val);
}

function Stat({ stat, run }) {
  const value = useCountUp(stat.noCount ? 0 : stat.target, run, stat.decimals || 0);
  return (
    <div className="statbar__item">
      <div className="statbar__icon">
        <PhosphorIcon name={stat.icon} size={26} />
      </div>
      <div className="statbar__num">
        {stat.noCount ? stat.display : (
          <>
            {stat.prefix || ""}{value}{stat.suffix || ""}
          </>
        )}
      </div>
      <div className="statbar__label">{stat.label}</div>
    </div>
  );
}

export default function StatBar() {
  const ref = useRef(null);
  const [run, setRun] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      setRun(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setRun(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="statbar" ref={ref} aria-label="Cloudare by the numbers">
      <div className="statbar__inner">
        {STATS.map((s) => (
          <Stat key={s.label} stat={s} run={run} />
        ))}
      </div>
    </section>
  );
}
